/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { Main } from '@atlaskit/navigation-system/layout/main';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { TopNav } from '@atlaskit/navigation-system/layout/top-nav';
import { DebugPanel } from '@atlassian/twg-microsite-experience/DebugPanel';
import {
	NavigationBar,
	NAV_BAR_HEIGHT_PX,
} from '@atlassian/twg-microsite-experience/NavigationBar';
import { PageErrorView } from '@atlassian/twg-microsite-experience/PageErrorView';
import { SentryErrorBoundary } from '@atlassian/twg-microsite-experience/sentry/error-boundary';

const NAV_BAR_HEIGHT_PX_STATIC = 88 satisfies typeof NAV_BAR_HEIGHT_PX;

const styles = cssMap({
	contentScrollLayout: {
		'--twg-content-dynamic-height': `calc(100dvh - ${NAV_BAR_HEIGHT_PX_STATIC}px)`,
		'--twg-content-height': `calc(100vh - ${NAV_BAR_HEIGHT_PX_STATIC}px)`,
	},
});

type MicrositeLayoutProps = {
	children: ReactNode;
	testId?: string;
};

export const MicrositeLayout = ({
	children,
	testId = 'twg-microsite-layout',
}: MicrositeLayoutProps): JSX.Element => {
	return (
		<SentryErrorBoundary
			loggerName="micrositeShell.errorBoundary"
			fallback={
				<Root>
					<Main>
						<PageErrorView />
					</Main>
				</Root>
			}
		>
			<div css={styles.contentScrollLayout} data-testid={testId}>
				<Root>
					<TopNav height={NAV_BAR_HEIGHT_PX} testId="twg-microsite-top-nav">
						<NavigationBar />
					</TopNav>
					<Main testId="twg-microsite-main">{children}</Main>
					<DebugPanel />
				</Root>
			</div>
		</SentryErrorBoundary>
	);
};
